import { ICanvasRenderingEngine } from '@adaptiva/canvas';
import { Vector2 } from '@adaptiva/core/space';
import { UiService } from '@adaptiva/core/ui';
import { IMemoryPipelineNode } from '@adaptiva/data-service';
import { inject, Injectable } from '@angular/core';
import { MemoryPipelineNodeEntity } from '../../entities';

@Injectable()
export class MemoryPipelineFormattingService {

	public verticalSpacing: int = 25;

	public horizontalSpacing: int = 25;

	public margin: int = 12;

	public horizontalPadding: int = 100;

	public verticalPadding: int = 100;

	public get verticalOffset(): int {
		return this.verticalSpacing + (this.margin * 2);
	}

	public get fontSize(): float {
		return this.ui.Spacing.SPACING_MEDIUM_NUMERIC * this.ui.BASE_UNIT_PX;
	}

	public get padding(): float {
		return this.ui.Spacing.SPACING_MEDIUM_NUMERIC * this.ui.BASE_UNIT_PX;
	}

	public ui: UiService = inject(UiService);

	public engine: ICanvasRenderingEngine = inject(ICanvasRenderingEngine);

	public moveNodeGroup(_node: MemoryPipelineNodeEntity, _offset: Partial<Vector2>): void {
		_node.position.add(_offset);
		_node.node.clients.forEach((_child: IMemoryPipelineNode) => {
			const _childEntity: MemoryPipelineNodeEntity = this.engine.getEntity(_child.hostName);
			this.moveNodeGroup(_childEntity, _offset);
		});
	}

	public formatNodes(_context: CanvasRenderingContext2D, _node: IMemoryPipelineNode): int {
		if (!_node.clients.length) {
			const _width: int = this.getNodeWidth(_context, _node);
			_node.width = _width;
			return _width;
		}

		if (_node.clients.length === 1) {
			const _singleChild: IMemoryPipelineNode = _node.clients[0];

			_singleChild.xOffset = 0;

			return _node.width = Math.max(this.formatNodes(_context, _singleChild), this.getNodeWidth(_context, _node));
		}

		let _nodeWidth: int = -this.horizontalPadding;

		const _childWidths: int[] = _node.clients.map((_child: IMemoryPipelineNode) => {
			const _aChildWidth: int = this.formatNodes(_context, _child);
			_nodeWidth += _aChildWidth + this.horizontalPadding;
			return _aChildWidth;
		});

		const _oddChildCount: boolean = _node.clients.length % 2 === 1;
		// when child count is even, _middle is the first index on the right side
		const _middle: int = Math.floor(_node.clients.length / 2);

		for (let i: int = 0; i < _node.clients.length; i++) {
			const _child: IMemoryPipelineNode = _node.clients[i];
			let _offset: int = 0;

			if (_oddChildCount) {
				if (i === _middle) {
					_child.xOffset = 0;
					continue;
				} else {
					if (i < _middle)
						_offset -= (_node.clients[_middle].width / 2);
					else
						_offset += (_node.clients[_middle].width / 2);
				}
			} else {
				if (i < _middle)
					_offset += this.horizontalPadding / 2;
				else
					_offset -= this.horizontalPadding / 2;
			}

			_offset += (i < _middle) ?
				this.beforeMiddle(_child, i, _childWidths, _middle) :
				this.afterMiddle(_child, i, _childWidths, _middle + (_oddChildCount ? 1 : 0));

			_child.xOffset = _offset;
		}

		_node.leftOffset = _node.clients[0].xOffset;
		_node.rightOffset = _node.clients[_node.clients.length - 1].xOffset;
		_node.width = _nodeWidth;

		return _node.width;
	}

	public beforeMiddle(_node: IMemoryPipelineNode, _index: int, _childWidths: int[], _middle: int): int {
		let _offset: int = _index === 0 && _node.rightOffset ?
			this.sumRightOffsets(_node) :
			_node.width / 2;

		for (let i: int = _index + 1; i < _middle; i++) {
			const _siblingWidth: int = _childWidths[i];
			_offset += _siblingWidth + this.horizontalPadding;
		}

		_offset += this.horizontalPadding;

		return -_offset;
	}

	public afterMiddle(_node: IMemoryPipelineNode, _index: int, _childWidths: int[], _middle: int): int {
		let _offset: int = _index === _childWidths.length - 1 && _node.leftOffset ?
			-this.sumLeftOffsets(_node) :
			_node.width / 2;

		for (let i: int = _middle; i < _index; i++) {
			const _siblingWidth: int = _childWidths[i];
			_offset += _siblingWidth + this.horizontalPadding;
		}

		_offset += this.horizontalPadding;

		return _offset;
	}

	public sumRightOffsets(_node: IMemoryPipelineNode): int {
		if (_node.clients.length)
			return (_node.rightOffset || 0) + this.sumRightOffsets(_node.clients[_node.clients.length - 1]);
		else
			return this.horizontalPadding;
	}

	public sumLeftOffsets(_node: IMemoryPipelineNode): int {
		if (_node.clients.length)
			return (_node.leftOffset || 0) + this.sumLeftOffsets(_node.clients[0]);
		else
			return -this.horizontalPadding;
	}

	public getNodeWidth(_context: CanvasRenderingContext2D, _node: IMemoryPipelineNode): int {
		const _textWidth: int = _context.measureText(_node.hostName).width;
		return Math.floor(
			this.padding + // left padding
			this.fontSize + // space for icon
			this.padding + // spacing after icon
			_textWidth + // space for text
			this.padding // right padding
		);
	}

	public applyRelativePositions(_nodeEntity: MemoryPipelineNodeEntity): void {
		const x: int = _nodeEntity.parent ? _nodeEntity.parent.position.x + _nodeEntity.node.xOffset : 0;
		const y: int = _nodeEntity.parent ? _nodeEntity.parent.position.y + this.verticalPadding : 0;
		_nodeEntity.position = new Vector2(x, y);

		_nodeEntity.children.forEach((_childEntity: MemoryPipelineNodeEntity) => {
			this.applyRelativePositions(_childEntity);
		});
	}
}
